/**
 * The CLIENT twin of `libs/common/src/sectionOverlays.ts` (section-overlays
 * spec §3).
 *
 * App code cannot import that package, so these are hand-kept copies. What
 * keeps them honest is that both sides pin the SAME hash vector in their own
 * test (`84c1e4c5`): drift in the canonical string or in the FNV loop turns
 * exactly one of the two red, and names which side moved.
 */
import { AS_BUILT_PLANE_PREFIX, DESIGN_PLANE_PREFIX, levelName } from './sectionModel';
import type { SectionOverlays, SectionOverlaysJson } from './types';

export type SectionOverlayKind = 'LAYER_SURFACE' | 'RUN_SECTIONS';

export interface SectionOverlaySetting {
  id: string;
  kind: SectionOverlayKind;
  layerId: string;
  /** Surface name (LandXML/DXF/GeoJSON) or plane name (RUN_SECTIONS); null for a point cloud. */
  ref: string | null;
  /** Point clouds only: `min` | `mean` | `median` | `p05` | `max`. */
  pcMethod: string | null;
  hidden: boolean;
}

export const MAX_SECTION_OVERLAYS = 8;

/** Twin of `libs/common/src/sectionOverlays.ts::sectionOverlayId`. */
export function sectionOverlayId(s: Pick<SectionOverlaySetting, 'kind' | 'layerId' | 'ref' | 'pcMethod'>): string {
  if (s.kind === 'RUN_SECTIONS') return `sib:${s.layerId}:${s.ref ?? ''}`;
  return s.ref != null ? `xml:${s.layerId}:${s.ref}` : `pc:${s.layerId}:${s.pcMethod ?? 'min'}`;
}

/**
 * Twin of `libs/common/src/sectionOverlays.ts::sectionOverlaysHash` — same
 * canonical string, same FNV-1a.
 *
 * A staleness token, not a security hash: it has to be cheap, synchronous and
 * identical in Node and the browser, and a collision costs one unnecessary
 * re-sample. `hidden` is excluded — hiding never re-samples.
 */
export function sectionOverlaysHash(list: readonly SectionOverlaySetting[]): string {
  const canonical = JSON.stringify(list.map((o) => [o.id, o.kind, o.layerId, o.ref ?? null, o.pcMethod ?? null]));
  let h = 0x811c9dc5;
  for (let i = 0; i < canonical.length; i++) {
    h ^= canonical.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16).padStart(8, '0');
}

/**
 * The three axes an overlays file has to agree with the run on before a single
 * one of its planes may be drawn — the CLIENT twin of
 * `processReport/stationOffsets.ts::overlaysMatchRun`.
 *
 * The settings hash says WHICH surfaces a file describes; this says whether
 * its rows and columns are the rows and columns of the section on screen. They
 * are independent failures: a file sampled for exactly the right surfaces, on
 * the grid of a run that has since been re-analysed at a different resolution
 * or with different swath ends, decodes cleanly and draws every reference at
 * the wrong offset — the worst kind of wrong, because it looks right.
 *
 * `lineSource` is the row vocabulary: `STATIONS` addresses true station lines
 * (the same index the run's own `station_sections.f32` uses), `BIN_CENTRES`
 * addresses station BINS. Drawing one against the other puts every reference
 * at the wrong chainage.
 */
export function overlaysMatchRun(
  meta: Pick<SectionOverlaysJson, 'lineSource' | 'nStationLines' | 'nOffsets'>,
  run: { trueSections: boolean; nStationLines: number; nOffsets: number },
): boolean {
  if (meta.lineSource !== (run.trueSections ? 'STATIONS' : 'BIN_CENTRES')) return false;
  if (meta.nOffsets !== run.nOffsets) return false;
  return meta.nStationLines === run.nStationLines;
}

export type OverlayState = 'drawn' | 'pending' | 'failed';

/**
 * What the popover and the legend say about each configured overlay.
 *
 * Three states, and the distinction between the last two is the whole point:
 * `pending` means this overlay has not been sampled yet (no file at all, or a
 * file that simply does not carry this id) and the reader should expect a line
 * to appear; `failed` means it tried and could not, and the reader should go
 * and fix something. Collapsing them into one "no line" would leave a
 * permanent failure looking like a slow job.
 *
 * The SETTINGS HASH is deliberately NOT consulted here (spec §7.4). An overlay
 * id encodes the source layer, the surface/plane and the reduction, and the
 * file's planes are sampled on the run's own grid — so a plane whose id is in
 * the file is the same line however many OTHER overlays have been added or
 * removed since. Hiding every drawn overlay because a ninth was just added
 * blanked the drawing for the length of a job on the strength of a token that
 * says nothing about the planes already present. Ids the file does not carry
 * are `pending`, which is the whole of what a stale file means.
 *
 * The caller still owns two verdicts this function cannot see: a fetch/decode
 * failure, and a file whose grid is not this run's (`overlaysMatchRun`). Both
 * are file-wide and both need a translator, so both are applied outside.
 */
export function overlayStates(
  settings: readonly SectionOverlaySetting[],
  overlays: SectionOverlays | null,
): Record<string, { state: OverlayState; error?: string | null }> {
  const out: Record<string, { state: OverlayState; error?: string | null }> = {};
  for (const s of settings) {
    const plane = overlays?.meta.planes.find((p) => p.id === s.id);
    if (!plane) out[s.id] = { state: 'pending' };
    // `error: null` — NOT a `'failed'` sentinel. This module is pure and has
    // no `t`, so a placeholder invented here would be an untranslated English
    // literal on its way to the DOM. A caller with a translator substitutes
    // its own generic reason; a caller without one still gets `state`.
    else if (!plane.ok) out[s.id] = { state: 'failed', error: plane.error };
    else out[s.id] = { state: 'drawn' };
  }
  return out;
}

// ---------------------------------------------------------------------------
// Client-only, from here down. Nothing below has a twin in
// `libs/common/src/sectionOverlays.ts`: it is what the POPOVER needs to offer
// a choice in names a reader recognises, and the server never asks these
// questions — it is handed the answers as a settings list.
// ---------------------------------------------------------------------------

/**
 * The slice of a project layer this module reads. Structurally satisfied by
 * `LayerData` (the `layers(projectId)` row) — declared here rather than
 * imported so a pure lib module keeps no dependency on the atom layer, and so
 * a test can hand it four fields instead of forty.
 */
export interface OverlayCandidateLayer {
  id: string;
  name: string;
  type: string;
  metadata?: unknown;
}

/** Another station run whose sections were cut on the SAME alignment. */
export interface OverlayCandidateRun {
  layerId: string;
  layerName: string;
  /** That run's own plane names — `['zTop','zBottom']`, or a stack's 2N. */
  planes: string[];
}

/** A layer carrying a surface the worker knows how to cut at a station. */
export interface OverlayCandidateSurface {
  layerId: string;
  layerName: string;
  kind: 'LANDXML' | 'DXF' | 'GEOJSON' | 'POINTCLOUD';
}

/** What `overlayCandidates` reads off a layer's untyped `metadata` JSON. */
interface OverlayHostMetadata {
  type?: string;
  stationVolumes?: {
    alignment?: { sourceHash?: string };
    sections?: { planes?: string[] } | null;
  } | null;
  layerStack?: {
    stationParams?: { alignmentSourceHash?: string } | null;
    sections?: { planes?: string[] } | null;
  } | null;
}

/**
 * The two lists the popover offers, in one pass over the project's layers.
 *
 * The run test is a HASH comparison and nothing else. Two runs can name the
 * same road, quote the same chainages and still have been measured over
 * different geometry — a re-imported alignment, an edited vertex — and their
 * offset columns then do not line up. Drawing one on the other would be a
 * silent lie on a drawing someone signs, so only an exact
 * `alignment.sourceHash` match is offered.
 *
 * A matching run is a RUN and never also a surface: a stack result is a MODEL
 * layer and would otherwise appear in both lists, offering the same data by
 * two different routes with two different meanings.
 */
export function overlayCandidates(
  layers: readonly OverlayCandidateLayer[],
  resultLayerId: string,
  sourceHash: string | null,
): { runs: OverlayCandidateRun[]; surfaces: OverlayCandidateSurface[] } {
  const runs: OverlayCandidateRun[] = [];
  const surfaces: OverlayCandidateSurface[] = [];
  for (const l of layers) {
    if (l.id === resultLayerId) continue;
    const m = (l.metadata ?? {}) as OverlayHostMetadata;
    const hash = m.stationVolumes?.alignment?.sourceHash ?? m.layerStack?.stationParams?.alignmentSourceHash ?? null;
    if (sourceHash && hash === sourceHash) {
      // A run made before true station sections existed declares no plane
      // names; its file is the original two. Falling back keeps those runs
      // offerable rather than silently absent from the list.
      const planes = m.stationVolumes?.sections?.planes ?? m.layerStack?.sections?.planes ?? ['zTop', 'zBottom'];
      runs.push({ layerId: l.id, layerName: l.name, planes });
      continue;
    }
    if (l.type === 'POINTCLOUD') surfaces.push({ layerId: l.id, layerName: l.name, kind: 'POINTCLOUD' });
    else if (l.type === 'VECTOR') surfaces.push({ layerId: l.id, layerName: l.name, kind: 'GEOJSON' });
    else if (l.type === 'MODEL' && (m.type === 'LANDXML' || m.type === 'DXF'))
      surfaces.push({ layerId: l.id, layerName: l.name, kind: m.type });
  }
  return { runs, surfaces };
}

/**
 * The five strings a name is assembled from, injected rather than looked up.
 *
 * This module is pure and has no translator, and every one of these is a
 * PLANE KEY or a METHOD CODE in the file — `zTop`, `asbuilt:Base`, `p05`.
 * Making the bundle required rather than defaulted is the point: there is no
 * "no translator" fallback that is not a raw key on screen, and a raw key is
 * exactly what findings 1 and 2 were about.
 *
 * `overlayLabelText(t)` below builds it, so the popover's plane DROPDOWN and
 * the row's NAME cannot drift into calling the same plane two things.
 */
export interface OverlayLabelText {
  /** `zTop` — the run's upper surface. */
  planeTop: string;
  /** `zBottom` — the run's lower surface. */
  planeBottom: string;
  /** `asbuilt:Base` → "Base (as-built)". */
  planeAsBuilt: (level: string) => string;
  /** `design:Base` → "Base (design)". */
  planeDesign: (level: string) => string;
  /** `p05` → "5th percentile". */
  method: (m: string) => string;
}

/**
 * The translator this module needs, in the only shape a PURE module can state
 * it: a key in, a string out.
 *
 * i18next's `TFunction` is typed against the project's key UNION (see
 * `app/src/core/i18next.d.ts`), so under `strictFunctionTypes` it is NOT
 * assignable to a `(key: string, …)` parameter — a function accepting any
 * string cannot stand in for one accepting only the known keys. Importing that
 * union here would tie a pure, `t`-free module to the i18n layer and cost the
 * three-line fake its own test passes in. So the two vocabularies meet at one
 * named seam instead: `asOverlayTranslate`.
 */
export type OverlayTranslate = (key: string, vars?: Record<string, unknown>) => string;

/**
 * The ONE place the `TFunction` → `OverlayTranslate` cast is written.
 *
 * Named rather than inlined so it is greppable and so no call site invents its
 * own `as unknown as` — every key this module asks for is a real key in
 * `project.json`, pinned by `overlayLabelText`'s own test and by the i18n
 * completeness test, and that is what makes the cast safe.
 */
export const asOverlayTranslate = (t: unknown): OverlayTranslate => t as OverlayTranslate;

/** The one place the five i18n keys behind `OverlayLabelText` are named. */
export const overlayLabelText = (t: OverlayTranslate): OverlayLabelText => ({
  planeTop: t('stationVolumes.section.overlays.planeTop'),
  planeBottom: t('stationVolumes.section.overlays.planeBottom'),
  planeAsBuilt: (name) => t('stationVolumes.section.overlays.planeAsBuilt', { name }),
  planeDesign: (name) => t('stationVolumes.section.overlays.planeDesign', { name }),
  method: (m) => t(`surfaceDifference.pcMethods.${m}`),
});

/**
 * One plane of a run, in words.
 *
 * A stack partitions its 2N planes by PREFIX, so `asbuilt:Base` and
 * `design:Base` are two different lines through the same level — stripping
 * the prefix and stopping there (which `levelName` alone does) renders both
 * as "Base" and leaves the reader unable to tell the surveyed line from the
 * designed one. The two-surface runs that predate the stack name their planes
 * `zTop`/`zBottom`, which are file keys and not names at all.
 */
export function planeDisplayName(plane: string, text: OverlayLabelText): string {
  if (plane === 'zTop') return text.planeTop;
  if (plane === 'zBottom') return text.planeBottom;
  if (plane.startsWith(AS_BUILT_PLANE_PREFIX)) return text.planeAsBuilt(levelName(plane));
  if (plane.startsWith(DESIGN_PLANE_PREFIX)) return text.planeDesign(levelName(plane));
  // A plane name from neither vocabulary is already free text the worker was
  // given; it is the one case where echoing it is right.
  return plane;
}

/**
 * The name for one configured overlay, derived from the project's layers.
 *
 * Deliberately the SAME two-part shape the server writes into the overlays
 * file (`preprocess.ts` :: `${src.name} · <that same plane/method name>`), so
 * the row does not rename itself the moment the sample lands — a name that
 * changes under the reader is worse than a name that arrives late.
 *
 * `null`, never an id, when the source layer has left the project: a caller
 * with a translator substitutes a positional "Overlay 2". An id on screen is
 * a key wearing a name's clothes and tells the reader nothing about which of
 * their overlays broke.
 */
export function overlayLabel(
  s: Pick<SectionOverlaySetting, 'kind' | 'layerId' | 'ref' | 'pcMethod'>,
  layers: readonly OverlayCandidateLayer[],
  text: OverlayLabelText,
): string | null {
  const layer = layers.find((l) => l.id === s.layerId);
  if (!layer) return null;
  const part =
    s.kind === 'RUN_SECTIONS'
      ? s.ref
        ? planeDisplayName(s.ref, text)
        : null
      : // A LandXML/DXF/GeoJSON surface carries its own name; a point cloud
        // has none and is identified by the reduction it was sampled with.
        (s.ref ?? (s.pcMethod ? text.method(s.pcMethod) : null));
  return part ? `${layer.name} · ${part}` : layer.name;
}
